import React, { useState } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_PXEMConverter() {
  const [pxValue, setPxValue] = useState(16);
  const [basePx, setBasePx] = useState(16);

  const emValue = (pxValue / basePx).toFixed(2);
  const pxFromEm = (emValue * basePx).toFixed(2);

  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📏 CSS PX ↔ EM Converter</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS uses different length units. <code>px</code> is an absolute unit,
        while <code>em</code> is a relative unit based on the font-size of the
        parent element. Converting between them helps create responsive designs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <b>px</b>: Fixed pixel value (absolute unit).
          </li>
          <li>
            <b>em</b>: Relative to the font-size of the parent element.
          </li>
          <li>
            Formula: <code>em = px ÷ base-font-size</code>
          </li>
          <li>
            Example: <code>16px ÷ 16px = 1em</code>,{" "}
            <code>24px ÷ 16px = 1.5em</code>
          </li>
        </ul>
      </div>

      {/* Converter Tool */}
      <div className="tutorial-section">
        <h2 className="section-title">⚙️ Live Converter</h2>

        <div style={{ marginBottom: "1rem" }}>
          <label>
            Base Font Size (px):{" "}
            <input
              type="number"
              value={basePx}
              onChange={(e) => setBasePx(Number(e.target.value))}
              style={{ width: "80px", marginLeft: "10px" }}
            />
          </label>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label>
            Value in PX:{" "}
            <input
              type="number"
              value={pxValue}
              onChange={(e) => setPxValue(Number(e.target.value))}
              style={{ width: "80px", marginLeft: "10px" }}
            />
          </label>
        </div>

        <p>
          <b>Converted Value:</b> {pxValue}px = {emValue}em (base {basePx}px)
        </p>

        <div
          style={{
            marginTop: "1rem",
            padding: "1rem",
            borderRadius: "8px",
            backgroundColor: "#f1f5f9",
            fontSize: `${emValue}em`,
          }}
        >
          Preview Text — This text scales with your EM value!
        </div>
      </div>

      {/* Example Code */}
      <div className="tutorial-section">
        <h2 className="section-title">📝 Example CSS Code</h2>
        <CodeCopyBlock
          code={`/* Base font-size is 16px */
p {
  font-size: 1em;   /* 16px */
}

h1 {
  font-size: 2em;   /* 32px */
}

.small {
  font-size: 0.75em;  /* 12px */
}`}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <code>rem</code> (relative to root font-size).
          </li>
          <li>
            Use <code>em</code> for scalable typography and spacing.
          </li>
          <li>
            Combine <code>em</code> with media queries for responsiveness.
          </li>
          <li>
            Try setting <code>html {`{ font-size: 62.5%; }`}</code> for easy
            rem/em math.
          </li>
        </ul>
      </div>
    </div>
  );
}
